import { isMaintenanceResponse } from '../maintenance/response';
/**
 * replayCore — the outbox replay for the kinds the service worker can send
 * with no tab open (offline reliability §1): daily logs (plain and with
 * photos), form submissions, incident reports (plain and with photos).
 *
 * Pure over its deps — no React, no window, no axios — so it bundles into
 * src/sw.ts. The app's replayRegistry delegates these kinds here and the
 * worker's sync handler calls it directly: ONE implementation of each kind's
 * idempotency (offline_id) and multipart contract. The only difference between
 * the two callers is the transport (`ReplayHttp`): the app's axios client with
 * its CSRF/cookie handling, or `fetch` with `credentials: 'include'`.
 *
 * Progress is persisted after every step (create → each photo → submit) so a
 * replay that dies mid-way resumes instead of duplicating, and each save
 * renews the row's lease — whoever is replaying keeps owning the row.
 */
import type { Table } from 'dexie';
import { LEASE_MS, type MutationEntry, type OfflineAttachment } from './db';
import { deleteAttachmentsForOutbox } from './attachments';
import { MAX_RETRY_ATTEMPTS } from './retryBackoff';
import type { ApiResponse } from '@demoops/shared-types';

// ---------------------------------------------------------------------------
// Kinds
// ---------------------------------------------------------------------------

export const REPLAY_CORE_KINDS = [
  'dailyLog.upsert',
  'dailyLog.full',
  'formSubmission.full',
  'incident.report',
  'incident.report.full',
] as const;

export type ReplayCoreKind = (typeof REPLAY_CORE_KINDS)[number];

export function isReplayCoreKind(kind: string): kind is ReplayCoreKind {
  return (REPLAY_CORE_KINDS as readonly string[]).includes(kind);
}

// ---------------------------------------------------------------------------
// Payloads (unchanged shapes — features import these via replayRegistry)
// ---------------------------------------------------------------------------

// Offline form-submission payload (files stored as Blobs in IndexedDB).
export interface OfflineFormSubmission {
  submission: { template_id: string; project_id: string | null; data: Record<string, unknown>; offline_id: string };
  signatures: { field_name: string; signer_name: string; signature_data: string }[];
  attachments: { field_name: string; filename: string; attachmentId: string; blob?: Blob }[];
}
export interface FormProgress {
  submissionId?: string;
  signaturesDone?: number;
  attachmentsDone?: number;
  submitted?: boolean;
}

// Offline daily-log-with-photos payload (blobs in the attachment store).
export interface OfflineDailyLogFull {
  // offline_id is REQUIRED: if the upsert POST succeeds but the progress save
  // fails, the next replay re-POSTs — only offline_id makes that idempotent.
  upsert: Record<string, unknown> & { offline_id: string };
  // Deliberately no inline `blob?: Blob` (unlike OfflineFormSubmission's
  // legacy-compat field) — blobs always come from the attachment store here.
  attachments: { field_name: string; filename: string; attachmentId: string }[];
}
export interface DailyLogFullProgress {
  logId?: string;
  attachmentsDone?: number;
}

// Offline incident-report-with-photos payload (blobs in the attachment store).
export interface OfflineIncidentFull {
  // offline_id is REQUIRED: the create POST is idempotent on offline_id, so a
  // re-POST after a failed progress save never creates a duplicate incident.
  incident: Record<string, unknown> & { offline_id: string };
  // Blobs always come from the attachment store here (no inline `blob?`).
  attachments: { field_name: string; filename: string; attachmentId: string }[];
}
export interface IncidentFullProgress {
  incidentId?: string;
  attachmentsDone?: number;
}

// ---------------------------------------------------------------------------
// Deps and results
// ---------------------------------------------------------------------------

/** The transport. `body` is a JSON-able object, a FormData (multipart — the
 *  transport must NOT set Content-Type itself), or undefined for a bodiless
 *  POST. Resolves with the parsed response body (`{ data: {...} }` envelope);
 *  rejects on HTTP failure with an error exposing `status` and/or
 *  `response.status` (+ `response.data` for the server's message). */
export interface ReplayHttp {
  post(url: string, body?: unknown): Promise<unknown>;
}

export interface ReplayDb {
  mutations: Table<MutationEntry, number>;
  attachments: Table<OfflineAttachment, string>;
}

export interface ReplayDeps {
  http: ReplayHttp;
  db: ReplayDb;
  /** Clock, for lease stamps. */
  now: () => number;
}

export type ReplayResult =
  | { outcome: 'done'; progress?: unknown }
  /** Try again later (network, 5xx, 408/429, a contract-first 404). `error`
   *  is the raw failure for callers that rethrow it. */
  | { outcome: 'retry'; message: string; status?: number; error?: unknown; progress?: unknown }
  /** Retrying cannot help (4xx); `message` is what the Sync Center shows. */
  | { outcome: 'terminal'; message: string; status?: number; progress?: unknown };

// ---------------------------------------------------------------------------
// Error classification (duck-typed — no axios in the worker bundle; axios
// errors satisfy the same shape, and the api client's enhanced Error carries
// `status` + `response` too)
// ---------------------------------------------------------------------------

export function errorStatus(err: unknown): number | undefined {
  const e = err as { response?: { status?: number }; status?: number } | null | undefined;
  return e?.response?.status ?? e?.status;
}

/** Server-authored message from the `{ error: { code, message } }` envelope. */
export function errorServerMessage(err: unknown): string | undefined {
  const msg = (err as { response?: { data?: { error?: { message?: unknown } } } } | null | undefined)
    ?.response?.data?.error?.message;
  return typeof msg === 'string' ? msg : undefined;
}

/**
 * 4xx (except 408/429) are terminal; network/5xx are retryable.
 *
 * PROJECT_ACCESS_POLICY rule 9 / P10: a mutation queued offline can be
 * replayed against a project the user has since LOST access to, and the server
 * rejects it. Access denials carry a plain reason so the queue explains
 * itself; other 4xx keep the status so existing behaviour is unchanged.
 */
function classify(err: unknown, progress?: unknown): ReplayResult {
  const status = errorStatus(err);
  if (status === 403) {
    return {
      outcome: 'terminal', status, progress,
      message: errorServerMessage(err) ?? 'You no longer have access to this project, so this change was not saved.',
    };
  }
  if (status === 404) {
    return {
      outcome: 'terminal', status, progress,
      message: errorServerMessage(err) ?? 'That record is no longer available, so this change was not saved.',
    };
  }
  if (status && status >= 400 && status < 500 && status !== 408 && status !== 429) {
    return { outcome: 'terminal', status, progress, message: String(status) };
  }
  return {
    outcome: 'retry', status, progress, error: err,
    message: err instanceof Error ? err.message : 'Sync error',
  };
}

const terminal = (message: string, progress?: unknown): ReplayResult => ({ outcome: 'terminal', message, progress });

// ---------------------------------------------------------------------------
// Shared steps
// ---------------------------------------------------------------------------

/** Persist resumable progress AND renew the lease — one write per step. */
async function saveProgress(deps: ReplayDeps, entry: MutationEntry, progress: unknown): Promise<void> {
  if (entry.id == null) return;
  await deps.db.mutations.update(entry.id, {
    progress: { ...(progress as Record<string, unknown>) },
    leaseUntil: new Date(deps.now() + LEASE_MS),
  });
}

/** Multipart photo upload — keep in sync with features/dailylogs/photos.ts
 *  uploadDailyLogPhoto and features/incidents/photos.ts (same contract). */
function photoForm(blob: Blob, filename: string, fieldName: string, offlineId: string | undefined): FormData {
  const fd = new FormData();
  fd.append('file', blob, filename);
  fd.append('field_name', fieldName);
  if (offlineId) fd.append('offline_id', offlineId);
  return fd;
}

type Created = ApiResponse<{ id: string }>;

// ---------------------------------------------------------------------------
// Kinds
// ---------------------------------------------------------------------------

// Nested daily-log upsert (crew/equipment/production + optional inline
// signature). Idempotent via offline_id (and/or UNIQUE(project_id, log_date))
// per contract §2.3.
async function replayDailyLogUpsert(entry: MutationEntry, deps: ReplayDeps): Promise<ReplayResult> {
  try {
    await deps.http.post('/api/v1/daily-logs', entry.payload);
    return { outcome: 'done' };
  } catch (e) { return classify(e); }
}

// Resumable: upsert (idempotent on offline_id) → photo uploads (deduped on
// (daily_log_id, offline_id) server-side). Progress persists after each step
// so a mid-flight failure resumes instead of duplicating.
async function replayDailyLogFull(entry: MutationEntry, deps: ReplayDeps): Promise<ReplayResult> {
  const p = entry.payload as OfflineDailyLogFull;
  const prog: DailyLogFullProgress = { ...((entry.progress as DailyLogFullProgress) ?? {}) };
  const save = () => saveProgress(deps, entry, prog);

  try {
    if (!prog.logId) {
      const created = (await deps.http.post('/api/v1/daily-logs', p.upsert)) as Created;
      prog.logId = created.data.id;
      await save();
    }
    const lid = prog.logId;

    for (let i = prog.attachmentsDone ?? 0; i < p.attachments.length; i++) {
      const a = p.attachments[i];
      const blob = (await deps.db.attachments.get(a.attachmentId))?.blob;
      if (!blob) return terminal(`missing attachment blob ${a.attachmentId}`, { ...prog });
      await deps.http.post(`/api/v1/daily-logs/${lid}/attachments`, photoForm(blob, a.filename, a.field_name, a.attachmentId));
      prog.attachmentsDone = i + 1;
      await save();
    }

    return { outcome: 'done', progress: { ...prog } };
  } catch (e) { return classify(e, { ...prog }); }
}

// Resumable: create → signatures → attachments → submit. Progress is persisted
// after each step so a mid-flight failure resumes instead of duplicating.
async function replayFormSubmissionFull(entry: MutationEntry, deps: ReplayDeps): Promise<ReplayResult> {
  const p = entry.payload as OfflineFormSubmission;
  const prog: FormProgress = { ...((entry.progress as FormProgress) ?? {}) };
  const save = () => saveProgress(deps, entry, prog);

  try {
    if (!prog.submissionId) {
      const created = (await deps.http.post('/api/v1/form-submissions', p.submission)) as Created;
      prog.submissionId = created.data.id;
      await save();
    }
    const sid = prog.submissionId;

    for (let i = prog.signaturesDone ?? 0; i < p.signatures.length; i++) {
      await deps.http.post(`/api/v1/form-submissions/${sid}/signatures`, p.signatures[i]);
      prog.signaturesDone = i + 1;
      await save();
    }

    for (let i = prog.attachmentsDone ?? 0; i < p.attachments.length; i++) {
      const a = p.attachments[i];
      // Inline blob is the legacy-compat path; the store is the normal one.
      const blob = a.blob ?? (await deps.db.attachments.get(a.attachmentId))?.blob;
      if (!blob) return terminal(`missing attachment blob ${a.attachmentId}`, { ...prog });
      await deps.http.post(`/api/v1/form-submissions/${sid}/attachments`, photoForm(blob, a.filename, a.field_name, a.attachmentId));
      prog.attachmentsDone = i + 1;
      await save();
    }

    if (!prog.submitted) {
      await deps.http.post(`/api/v1/form-submissions/${sid}/submit`);
      prog.submitted = true;
      await save();
    }
    return { outcome: 'done', progress: { ...prog } };
  } catch (e) { return classify(e, { ...prog }); }
}

// Quick incident report (Phase 9). offline_id dedupes replays server-side
// (200 == success). 404 stays RETRYABLE for this kind (conductor rule,
// contract-first rollout): a report queued before the endpoint ships must
// land after it does, not die as a terminal failure.
async function replayIncidentReport(entry: MutationEntry, deps: ReplayDeps): Promise<ReplayResult> {
  try {
    await deps.http.post('/api/v1/incidents', entry.payload);
    return { outcome: 'done' };
  } catch (e) {
    if (errorStatus(e) === 404) {
      return { outcome: 'retry', status: 404, error: e, message: e instanceof Error ? e.message : 'Sync error' };
    }
    return classify(e);
  }
}

// Resumable incident-report-with-photos: create (idempotent on offline_id) →
// photo uploads (deduped on (incident_id, offline_id) server-side).
async function replayIncidentReportFull(entry: MutationEntry, deps: ReplayDeps): Promise<ReplayResult> {
  const p = entry.payload as OfflineIncidentFull;
  const prog: IncidentFullProgress = { ...((entry.progress as IncidentFullProgress) ?? {}) };
  const save = () => saveProgress(deps, entry, prog);

  try {
    if (!prog.incidentId) {
      const created = (await deps.http.post('/api/v1/incidents', p.incident)) as Created;
      prog.incidentId = created.data.id;
      await save();
    }
    const iid = prog.incidentId;

    for (let i = prog.attachmentsDone ?? 0; i < p.attachments.length; i++) {
      const a = p.attachments[i];
      const blob = (await deps.db.attachments.get(a.attachmentId))?.blob;
      if (!blob) return terminal(`missing attachment blob ${a.attachmentId}`, { ...prog });
      await deps.http.post(`/api/v1/incidents/${iid}/attachments`, photoForm(blob, a.filename, a.field_name, a.attachmentId));
      prog.attachmentsDone = i + 1;
      await save();
    }

    return { outcome: 'done', progress: { ...prog } };
  } catch (e) { return classify(e, { ...prog }); }
}

const replayers: Record<ReplayCoreKind, (entry: MutationEntry, deps: ReplayDeps) => Promise<ReplayResult>> = {
  'dailyLog.upsert': replayDailyLogUpsert,
  'dailyLog.full': replayDailyLogFull,
  'formSubmission.full': replayFormSubmissionFull,
  'incident.report': replayIncidentReport,
  'incident.report.full': replayIncidentReportFull,
};

/** Replay one outbox row. Never throws for a replay failure — the outcome
 *  says what happened; a kind this module does not own is `terminal`. */
export async function replayEntry(entry: MutationEntry, deps: ReplayDeps): Promise<ReplayResult> {
  if (!isReplayCoreKind(entry.entityType)) {
    return terminal(`No replay handler for ${entry.entityType}`);
  }
  return replayers[entry.entityType](entry, deps);
}

// ---------------------------------------------------------------------------
// Settling a row — the one place a replay outcome becomes row state, used by
// the app's SyncProvider and the worker alike.
// ---------------------------------------------------------------------------

/**
 * Stamp the outcome onto the row `m` (as returned by claimMutation — its
 * `attempts` already counts this try) and release the lease. `done` also frees
 * the row's staged photo blobs.
 */
export async function applyReplayResult(db: ReplayDb, m: MutationEntry, result: ReplayResult): Promise<void> {
  if (m.id == null) return;
  if (result.outcome === 'done') {
    await db.mutations.update(m.id, { status: 'completed', leaseUntil: undefined });
    await deleteAttachmentsForOutbox(m.idempotencyKey, db);
    return;
  }
  if (result.outcome === 'terminal') {
    await db.mutations.update(m.id, { status: 'failed', error: `Rejected by server (${result.message})`, leaseUntil: undefined });
    return;
  }
  // Scheduled downtime must not exhaust the retry budget or discard local work.
  if (isMaintenanceResponse((result.error as { response?: { status?: number; headers?: unknown } } | undefined)?.response)) {
    await db.mutations.update(m.id, {
      status: 'pending', attempts: Math.max(1, m.attempts - 1),
      error: 'Database upgrade in progress; saved on this device and waiting to send.', leaseUntil: undefined,
    });
    return;
  }
  const willRetry = m.attempts < MAX_RETRY_ATTEMPTS;
  await db.mutations.update(m.id, { status: willRetry ? 'pending' : 'failed', error: result.message, leaseUntil: undefined });
}

// ---------------------------------------------------------------------------
// fetch transport (the service worker's; the app uses its axios client)
// ---------------------------------------------------------------------------

/** HTTP failure from the fetch transport, shaped like the app's api-client
 *  error so classification and the Sync Center read it the same way. */
export class ReplayHttpError extends Error {
  status: number;
  response: { status: number; data: unknown; headers?: Headers };

  constructor(status: number, data: unknown, headers?: Headers) {
    super(errorServerMessage({ response: { data } }) ?? `Request failed with status code ${status}`);
    this.name = 'ReplayHttpError';
    this.status = status;
    this.response = { status, data, headers };
  }
}

export interface FetchHttpOptions {
  /** `'include'` by default — the session cookie must ride along. */
  credentials?: RequestCredentials;
  /** Extra headers per request (the worker adds X-XSRF-TOKEN here). */
  headers?: () => Promise<Record<string, string>>;
}

export function createFetchHttp(fetchImpl: typeof fetch, options: FetchHttpOptions = {}): ReplayHttp {
  return {
    async post(url, body) {
      const headers: Record<string, string> = {
        Accept: 'application/json',
        ...(options.headers ? await options.headers() : {}),
      };
      let payload: BodyInit | undefined;
      if (body instanceof FormData) {
        payload = body; // the browser sets the multipart boundary
      } else if (body !== undefined) {
        headers['Content-Type'] = 'application/json';
        payload = JSON.stringify(body);
      }
      const res = await fetchImpl(url, {
        method: 'POST',
        body: payload,
        headers,
        credentials: options.credentials ?? 'include',
      });
      const text = await res.text();
      let data: unknown;
      if (text) {
        try { data = JSON.parse(text); } catch { data = text; }
      }
      if (!res.ok) throw new ReplayHttpError(res.status, data, res.headers);
      return data;
    },
  };
}
